// Searchable property picker, rendered as a Popover+Command combobox.
//
// Used by {@link AppointmentForm}. The selected row mimics the design comp
// at design/src/components/desktop/Appointments.jsx (lines 100-110): a
// 36×36 rounded thumbnail, building name on top, and a monospace meta line
// (`internalCode · ward · formattedPrice`) underneath.
//
// Selecting "Không xác định" at the top of the command list clears the
// selection. Filter text matches against buildingName, internalCode, and
// wardDisplayName.

import { useMemo, useState } from "react";
import { Building, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { AuthImg } from "@/components/auth-img";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { formatPrice } from "@/features/property/utils/format-price";
import type { Property } from "@/types/property";

const NO_PROPERTY_VALUE = "__NONE__";

/** Props for {@link PropertyPickerField}. */
export interface PropertyPickerFieldProps {
  /** Currently-selected propertyId; `null` means "not picked". */
  value: number | null;
  /** Full list of pickable properties; filtered client-side. */
  properties: readonly Property[];
  /** Mirrors the source `useProperties` `isPending` flag. */
  isPending: boolean;
  /** Selection callback. Pass `null` to clear. */
  onChange: (id: number | null) => void;
  disabled?: boolean | undefined;
}

/**
 * Render a searchable property picker tile. The selected-state row uses
 * the same compact card layout as the design comp's "Bất động sản" block.
 */
export function PropertyPickerField({
  value,
  properties,
  isPending,
  onChange,
  disabled,
}: PropertyPickerFieldProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => (value !== null ? properties.find((p) => p.id === value) ?? null : null),
    [value, properties],
  );

  return (
    // modal: the appointment-create Dialog's scroll-lock would otherwise make
    // this body-portaled list unscrollable by wheel.
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled || isPending}
          aria-label="Chọn bất động sản"
          className={cn(
            "flex w-full items-center gap-2.5 rounded-[9px] border border-border-strong bg-background px-2.5 py-2 text-left transition-colors",
            "hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring",
            "disabled:cursor-not-allowed disabled:opacity-60",
          )}
        >
          <PropertyThumb property={selected} />
          <div className="flex min-w-0 flex-1 flex-col">
            {selected !== null ? (
              <>
                <span className="truncate text-[14px] font-semibold leading-tight">
                  {selected.buildingName ?? `BĐS #${selected.id}`}
                </span>
                <span className="truncate font-mono text-[11px] text-muted-foreground">
                  {formatPropertyMeta(selected)}
                </span>
              </>
            ) : (
              <span className="text-[14px] font-medium text-muted-foreground">
                {isPending ? "Đang tải..." : "Chọn bất động sản"}
              </span>
            )}
          </div>
          <ChevronDown
            aria-hidden="true"
            className="size-3.5 shrink-0 text-muted-foreground"
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[320px] p-0"
      >
        <Command
          filter={(itemValue, search) => {
            // The CommandItem's `value` prop carries the searchable haystack
            // (see below). cmdk lowercases both sides for us; just contains.
            return itemValue.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Tìm theo tên, mã, khu vực..." />
          <CommandList>
            <CommandEmpty>Không có bất động sản phù hợp.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={NO_PROPERTY_VALUE}
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
                className="text-muted-foreground"
              >
                Không xác định
              </CommandItem>
              {properties.map((property) => {
                const haystack = [
                  property.buildingName,
                  property.internalCode,
                  property.wardDisplayName,
                ]
                  .filter((s): s is string => Boolean(s))
                  .join(" ")
                  .toLowerCase();
                return (
                  <CommandItem
                    key={property.id}
                    value={haystack || String(property.id)}
                    onSelect={() => {
                      onChange(property.id);
                      setOpen(false);
                    }}
                  >
                    <div className="flex w-full items-center gap-2.5">
                      <PropertyThumb property={property} />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-[14px] font-semibold leading-tight">
                          {property.buildingName ?? `BĐS #${property.id}`}
                        </span>
                        <span className="truncate font-mono text-[11px] text-muted-foreground">
                          {formatPropertyMeta(property)}
                        </span>
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Subviews
// ---------------------------------------------------------------------------

function PropertyThumb({ property }: { property: Property | null }) {
  if (property === null) {
    return (
      <div className="flex size-9 shrink-0 items-center justify-center rounded-[7px] bg-muted text-muted-foreground">
        <Building aria-hidden="true" className="size-4" />
      </div>
    );
  }
  if (property.thumbnailUrl !== null) {
    return (
      <div className="size-9 shrink-0 overflow-hidden rounded-[7px] bg-gradient-to-br from-slate-200 to-slate-300">
        <AuthImg
          src={property.thumbnailUrl}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }
  return (
    <div className="flex size-9 shrink-0 items-center justify-center rounded-[7px] bg-gradient-to-br from-slate-200 to-slate-300 text-slate-500">
      <Building aria-hidden="true" className="size-4" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPropertyMeta(property: Property): string {
  const parts: string[] = [];
  if (property.internalCode) parts.push(property.internalCode);
  if (property.wardDisplayName) parts.push(property.wardDisplayName);
  parts.push(formatPrice(property.price, property.listingType));
  return parts.join(" · ");
}
